// Route Middleware Component - User type-based route protection
import React, { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import userDataService, { DEFAULT_REDIRECTS } from '../../services/userDataService.js'
import { USER_TYPES } from '../../constants/userTypes.js'
import { clearAuthData } from '../../utils/sessionUtils.js'
import { ResolutionGuard } from '../../hooks/useScreenResolution.jsx'
import { isFirmUser, showFirmAccessToast } from '../../utils/firmPermissions.js'
// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontFamily: '"Red Hat Display", sans-serif'
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #E5E7EB',
        borderTop: '4px solid #3B82F6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</p>
    </div>
  </div>
)

// Helper function to get display name for user type
const getUserTypeDisplayName = (userType) => {
  // Map PET_SHOP_OWNER to SERVICE_PROVIDER for display
  if (userType === 'PET_SHOP_OWNER' || userType === USER_TYPES.SERVICE_PROVIDER) {
    return 'SERVICE_PROVIDER'
  }
  return userType
}

// Access Denied component
const AccessDenied = ({ userType, requiredTypes, onGoBack }) => {
  const displayUserType = getUserTypeDisplayName(userType)
  const displayRequiredTypes = requiredTypes.map(type => 
    type === USER_TYPES.SERVICE_PROVIDER || type === 'PET_SHOP_OWNER' ? 'SERVICE_PROVIDER' : type
  )
  
  return (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontFamily: '"Red Hat Display", sans-serif'
  }}>
    <div style={{ 
      textAlign: 'center', 
      maxWidth: '500px', 
      padding: '40px',
      background: '#FFFFFF',
      borderRadius: '16px',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        fontSize: '48px',
        marginBottom: '20px'
      }}>🚫</div>
      
      <h2 style={{ 
        fontSize: '24px', 
        fontWeight: 600, 
        color: '#EF4444', 
        marginBottom: '16px' 
      }}>
        Access Denied
      </h2>
      
      <p style={{ 
        color: '#6B7280', 
        marginBottom: '20px', 
        lineHeight: '1.5' 
      }}>
        You don't have permission to access this resource.
      </p>
      
      <div style={{
        background: '#F3F4F6',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#374151' }}>
          Your Account Type: <span style={{ color: '#3B82F6' }}>{displayUserType}</span>
        </p>
        <p style={{ margin: '0', color: '#6B7280' }}>
          Required: {displayRequiredTypes.join(' or ')}
        </p>
      </div>
      
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button
          onClick={onGoBack}
          style={{
            padding: '12px 24px',
            background: '#6B7280',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Go Back
        </button>
        
        <button
          onClick={() => {
            const defaultRedirect = DEFAULT_REDIRECTS[userType] || '/'
            window.location.href = defaultRedirect
          }}
          style={{
            padding: '12px 24px',
            background: '#3B82F6',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  </div>
)
}

// Main Route Middleware Component
const RouteMiddleware = ({ 
  children, 
  allowedUserTypes = [], 
  requireAuth = true,
  fallbackPath = null,
  minScreenWidth = null // Minimum screen width in pixels (e.g., 768, 1024)
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [userData, setUserData] = useState(null)
  const [error, setError] = useState(null)
  const location = useLocation()

  useEffect(() => {
    initializeUserData()
  }, [])

  const initializeUserData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Get user data
      const currentUserData = await userDataService.getCurrentUserData()
      
      if (currentUserData) {
        setUserData(currentUserData)
      } else {
        setError('Failed to load user data')
      }
    } catch (err) {
      console.error('Error initializing user data:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />
  }

  // Handle authentication requirement
  if (requireAuth) {
    // Check if user is authenticated
    if (!userDataService.isAuthenticated() || !userData) {
      // Store intended route for redirect after login
      clearAuthData();
      localStorage.setItem('intended_route', location.pathname)
      return <Navigate to="/auth/login" replace />
    }

    // Check user type permissions
    if (allowedUserTypes.length > 0) {
      const userType = userDataService.getUserType()
      
      if (!allowedUserTypes.includes(userType)) {
        // If user is ADMIN or CONTENT_ADMIN, redirect to admin dashboard instead of showing access denied
        if (userType === USER_TYPES.ADMIN || userType === USER_TYPES.CONTENT_ADMIN) {
          return <Navigate to="/admin/dashboard" replace />
        }
        
        // If user is CLINIC_MODERATOR trying to access social routes, redirect to their clinic
        // CLINIC_MODERATOR should only access clinic/medical features
        if (userType === USER_TYPES.CLINIC_MODERATOR && location.pathname.startsWith('/social')) {
          // Redirect to reception/clinic management (their main area)
          return <Navigate to="/reception" replace />
        }

        // FIRM users are never shown the generic AccessDenied screen.
        // Silently redirect to Connect (/social) and show a toast notification.
        if (isFirmUser(userType)) {
          showFirmAccessToast('This page is not available for Firm accounts.')
          return <Navigate to="/social" replace />
        }
        
        return (
          <AccessDenied 
            userType={userType}
            requiredTypes={allowedUserTypes}
            onGoBack={() => window.history.back()}
          />
        )
      }
    }

    // ----- FIRM-specific second-level checks (after allowedUserTypes passes) -----
    const userType = userDataService.getUserType()
    if (isFirmUser(userType)) {
      // Account Preferences ownership check:
      // FIRM users may only access their OWN account preferences.
      // Block if a ?userId= or ?user_id= query param references a different user.
      if (
        location.pathname.startsWith('/account/preferences') ||
        location.pathname.startsWith('/account/profile')
      ) {
        const qs = new URLSearchParams(location.search)
        const requestedUserId = qs.get('userId') || qs.get('user_id')
        if (requestedUserId) {
          const currentUser = userDataService.userData
          const currentId = String(currentUser?.id || currentUser?.user_id || '')
          if (currentId && requestedUserId !== currentId) {
            // Redirect to their own account preferences silently
            return <Navigate to="/account/preferences" replace />
          }
        }
      }
    }

    // Check route-specific permissions
    if (!userDataService.hasRoutePermission(location.pathname)) {
      const userType = userDataService.getUserType()
      const defaultRedirect = userDataService.getDefaultRedirect()
      
      return (
        <AccessDenied 
          userType={userType}
          requiredTypes={[`Access to ${location.pathname}`]}
          onGoBack={() => window.history.back()}
        />
      )
    }
  }

  // If fallback path is specified and user doesn't have access, redirect
  if (fallbackPath && userData) {
    const userType = userDataService.getUserType()
    const defaultRedirect = userDataService.getDefaultRedirect()
    
    if (!userDataService.hasRoutePermission(location.pathname)) {
      return <Navigate to={fallbackPath || defaultRedirect} replace />
    }
  }

  // Check screen resolution if minScreenWidth is specified
  if (minScreenWidth !== null && minScreenWidth > 0) {
    return (
      <ResolutionGuard minWidth={minScreenWidth}>
        {children}
      </ResolutionGuard>
    );
  }

  // Render children if all checks pass
  return children
}

// Higher-order component for easy route protection
export const withRouteProtection = (WrappedComponent, options = {}) => {
  const {
    allowedUserTypes = [],
    requireAuth = true,
    fallbackPath = null
  } = options

  return function ProtectedComponent(props) {
    return (
      <RouteMiddleware 
        allowedUserTypes={allowedUserTypes}
        requireAuth={requireAuth}
        fallbackPath={fallbackPath}
      >
        <WrappedComponent {...props} />
      </RouteMiddleware>
    )
  }
}

// Utility function to check if current user can access a route
export const canAccessRoute = (routePath, userType = null) => {
  if (!userType) {
    userType = userDataService.getUserType()
  }
  
  return userDataService.hasRoutePermission(routePath)
}

// Utility function to get user-specific redirect
export const getUserRedirect = (userType = null) => {
  if (!userType) {
    userType = userDataService.getUserType()
  }
  
  return DEFAULT_REDIRECTS[userType] || '/'
}

export default RouteMiddleware
